import { useTheme } from "@emotion/react";
import {
  AttachMoneyOutlined,
  BarChartOutlined,
  DashboardOutlined,
  ExpandMore,
  FolderOutlined,
  HomeOutlined,
  HubOutlined,
  MonitorHeart,
  PermContactCalendarOutlined,
  Phonelink,
  ReceiptOutlined,
  RequestQuoteOutlined,
  RouterOutlined,
  SecurityOutlined,
  ShareOutlined,
  SupportAgentOutlined,
  TextSnippetOutlined,
  TipsAndUpdatesOutlined,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Snackbar,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import api from "../../../../../../services/api";
import ErpChip from "../../../../../../components/erp/ErpChip";
import ErpFilter from "../../../../../../components/erp/ErpFilter";
import useErpSelecionado from "../../../../../../components/erp/useErpSelecionado";
import useStyles from "./styles";

// Ícone por nome de página. Mantido local (não compartilhado com o mapa do
// portal do cliente) seguindo o mesmo padrão de MenuPermissionTree/cardPermissao.
const iconMap = {
  Home: <HomeOutlined />,
  Dashboard: <BarChartOutlined />,
  Overview: <DashboardOutlined />,
  Saúde: <MonitorHeart />,
  Disponibilidade: <Phonelink />,
  Firewall: <SecurityOutlined />,
  "Transceptor óptico": <RouterOutlined />,
  ONUs: <RouterOutlined />,
  "Links e Sessões": <HubOutlined />,
  Flow: <ShareOutlined />,
  Tickets: <SupportAgentOutlined />,
  Financeiro: <AttachMoneyOutlined />,
  Faturas: <RequestQuoteOutlined />,
  NFs: <ReceiptOutlined />,
  Contratos: <ReceiptOutlined />,
  Relatórios: <TextSnippetOutlined />,
  Sugestões: <TipsAndUpdatesOutlined />,
};
const getIcon = (pagina) => iconMap[pagina] || <FolderOutlined />;

// Achata a árvore para contar quantas rotas existem/estão habilitadas
const contarRotas = (nodes = []) =>
  nodes.reduce(
    (acc, node) => {
      acc.total += 1;
      if (node.status) acc.habilitadas += 1;
      const filhos = contarRotas(node.children);
      acc.total += filhos.total;
      acc.habilitadas += filhos.habilitadas;
      return acc;
    },
    { total: 0, habilitadas: 0 },
  );

export default function RotasVisualizacaoCliente() {
  const theme = useTheme();
  const styles = useStyles(theme);

  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [snackbar, setSnackbar] = useState(null);
  // Cada cliente pertence a um ERP: a liberação das telas do portal é por ERP
  // (HUBSOFT A e B separados). A árvore mostra só as telas do tipo do ERP.
  const { erps, erpId, setErpId, carregado, variosErps } = useErpSelecionado();
  const erpSelecionado = erps?.find((item) => item.id === erpId);
  const erpAtualRef = useRef(erpId);
  erpAtualRef.current = erpId;

  useEffect(() => {
    if (!carregado) return;
    if (!erpId) {
      setTree([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    getRotas();
  }, [carregado, erpId]);

  const getRotas = async () => {
    const erpPedido = erpId;
    try {
      const response = await api.post(
        "/interno/find/rotasVisualizacaoCliente",
        {
          erpId: erpPedido,
        },
      );
      if (erpPedido !== erpAtualRef.current) return; // trocou de ERP no meio
      setTree(response?.data || []);
    } catch (error) {
      console.error(error);
      setSnackbar({
        children: "Error: Falha ao buscar as rotas do portal do cliente",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (node, novoStatus) => {
    setSavingId(node.id);
    try {
      const response = await api.post(
        "/interno/update/statusRotaVisualizacaoCliente",
        { id: node.id, status: novoStatus, erpId },
      );
      setTree(response?.data || []);
      setSnackbar({
        children: novoStatus
          ? `"${node.pagina}" liberada para o portal dos clientes ${erpSelecionado?.nome || ""}`
          : `"${node.pagina}" removida do portal dos clientes ${erpSelecionado?.nome || ""}`,
        severity: "success",
      });
    } catch (error) {
      setSnackbar({
        children:
          error?.response?.data?.error ||
          "Error: Falha ao atualizar a rota do portal",
        severity: "error",
      });
    } finally {
      setSavingId(null);
    }
  };

  const { total, habilitadas } = contarRotas(tree);

  const RowLabel = ({ node, child }) => (
    <Box sx={styles.rowLabel}>
      {getIcon(node.pagina)}
      <Typography sx={child ? styles.rowTextChild : styles.rowText}>
        {node.pagina}
      </Typography>
      {!node.status && (
        <Chip label="Desabilitada" size="small" sx={styles.chipDisabled} />
      )}
    </Box>
  );

  const RowSwitch = ({ node, disabledByParent }) => (
    <Tooltip
      title={
        disabledByParent
          ? "Habilite a rota pai para liberar esta rota"
          : node.status
            ? "Remover do portal do cliente"
            : "Liberar para o portal do cliente"
      }
    >
      <span>
        <Switch
          size="small"
          checked={!!node.status}
          disabled={disabledByParent || savingId === node.id}
          onChange={(event) => handleToggle(node, event.target.checked)}
        />
      </span>
    </Tooltip>
  );

  return (
    <>
      <Paper
        sx={styles.paper}
        elevation={theme.palette.mode === "dark" ? 0 : 3}
      >
        <Box sx={styles.headerRow}>
          <Box sx={styles.header}>
            <Box sx={styles.headerTitleBox}>
              <PermContactCalendarOutlined color="primary" />
              <Typography variant="h5" sx={styles.title}>
                Configuração de permissões do cliente
              </Typography>
              {/* Para qual ERP é esta liberação. */}
              <ErpChip erp={erpSelecionado} />
            </Box>
            {!loading && (
              <Chip
                label={`${habilitadas}/${total} rotas habilitadas`}
                size="small"
                sx={styles.counterChip}
              />
            )}
          </Box>
          <Typography sx={styles.subtitle}>
            Defina quais telas existem para o lado do cliente
            {erpSelecionado
              ? ` dos clientes do ERP ${erpSelecionado.nome}`
              : ""}
            .
          </Typography>
          {variosErps && (
            <ErpFilter
              erps={erps}
              value={erpId}
              onChange={(id) => id && setErpId(id)}
              limpavel={false}
              id="filtro-erp-rotas-cliente"
              sx={{ minWidth: 260, maxWidth: 360, mt: 1.5 }}
            />
          )}
        </Box>

        {carregado && !erpId && (
          <Alert severity="warning">
            Cadastre um ERP em Administração › Geral › Configuração interna para
            configurar as telas do portal do cliente.
          </Alert>
        )}
        {loading ? (
          <Box sx={styles.loadingContainer}>
            <CircularProgress size={32} thickness={4} />
          </Box>
        ) : (
          <Box sx={styles.treeContainer}>
            {tree?.map((node) =>
              node.children?.length > 0 ? (
                <Accordion
                  key={node.id}
                  variant="outlined"
                  disableGutters
                  sx={styles.accordion}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMore />}
                    sx={styles.accordionSummary}
                  >
                    <Box sx={styles.rowContent}>
                      <RowLabel node={node} />
                      <Box onClick={(e) => e.stopPropagation()}>
                        <RowSwitch node={node} disabledByParent={false} />
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={styles.accordionDetails}>
                    {node.children.map((child) => (
                      <Box key={child.id} sx={styles.childRow}>
                        <RowLabel node={child} child />
                        <RowSwitch
                          node={child}
                          disabledByParent={!node.status}
                        />
                      </Box>
                    ))}
                  </AccordionDetails>
                </Accordion>
              ) : (
                <Box key={node.id} sx={styles.leafRow}>
                  <RowLabel node={node} />
                  <RowSwitch node={node} disabledByParent={false} />
                </Box>
              ),
            )}
          </Box>
        )}
      </Paper>
      {!!snackbar && (
        <Snackbar
          open
          onClose={() => setSnackbar(null)}
          autoHideDuration={3000}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert {...snackbar} onClose={() => setSnackbar(null)} />
        </Snackbar>
      )}
    </>
  );
}
